<template src="./ProcessDetail.html"></template>

<script>
import _ from 'lodash';
import { ABLE_FLG, RESPONSIBLE_FLG, PROCESS_OPERATION, PROCESS_TYPE, TYPE_OF_WORKS, USER_ROLE_CD } from 'lib-tw-common';
import { $api } from '@/store/ApiClient';
import mixinProcessDetail from '@/utils/mixinProcessDetail.js';
import schemas from '@/dictionaries/processes/ecreq.json';
import milestoneSchema from '@/dictionaries/ecreqMilestoneSchema.json';

export default {
  name: 'ProcessDetail',
  mixins: [mixinProcessDetail],
  provide() {
    return {
      refreshProcess: this.fetch,
    }
  },
  data() {
    return {
      processId: this._processId.toUpperCase(),
      processType: PROCESS_TYPE.ECREQ,
      tradeManagement: null,
      schemas: _.cloneDeep(schemas),
      standardSchema: _.cloneDeep(schemas),
      rules: this.createRules(schemas.tables),
    };
  },
   computed: {
    simpleSchemas() {
      if (!this.isSimpleMode()) return this.schemas;

      const base = this.schemas;
      const tables = base.tables.map(table => ({
        ...table,
        groups: table.groups.map(group => ({
          ...group,
          children: (group.children || []).filter(c => c && (c.required === true || c.simpleItem === true)),
        })).filter(group => group.children.length > 0),
      })).filter(table => table.groups.length > 0);

      const hasRequiredChild = table => table.groups.some(group => group.children.some(c => c.required === true));
      tables.sort((a, b) => (hasRequiredChild(a) ? 0 : 1) - (hasRequiredChild(b) ? 0 : 1));

      return { ...base, tables };
    },
    milestoneSchema() {
      return milestoneSchema;
    }
  },
  methods: {
    // BFFからデータを取得
    fetch(updateCommentOnly = false) {
      // bff_ec_4 輸出船積依頼プロセス詳細初期表示BFF
      const params = {
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode: 'get_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}/initial',
          path: {
            entityId: this.entityId,
            processId: this.processId,
            processSeq: parseInt(this.processSeq)
          },
          query: {
            processTrx: this.versionNo,
          }
        },
        tag: this.pushCancelTag(),
      };

      $api
        .request(params)
        .then(res => {
          if (!this.checkEditPermission(res)) {
            return;
          }
          if(updateCommentOnly) {
            this.res.chats = res.chats;
          } else {
            if (!this.isArchive && res.tradeManagement.mailChatDocumentAbleFlg === ABLE_FLG.TRUE && !this.communicationDestinations.from) {
              this.getCommunicationDestinations();
            }
            this.res = res;
            // linkageGoodsTypeのレスポンスの階層をリクエストに合わせる
            this.res.goodsTypeMasterListForResponse = _.get(this.res, 'linkageGoodsLineType.goodsTypeMasterListForResponse') || [];
            this.res.linkageGoodsLineType = _.get(this.res, 'linkageGoodsLineType.goodsType') || [];
            this.cloneItems = this.cloneDeep(this.res);
            this.originalValue = _.cloneDeep(this.cloneItems);
            this.roles = {
              from: res.from,
              to: res.to,
              owner: res.owner
            };
            // Document signerプルダウン項目取得
            if (this.roles.from && this.roles.from.userRoleCd === USER_ROLE_CD.PIC) {
              this.$store.dispatch('GET_CO_DOCUMENT_SIGNERS', this.roles.from);
            }
            requestAnimationFrame(() => {
              this.tradeManagement = this.res.tradeManagement || {};
            });
          }
          if (_.last(this.res.chats)) {
            this.readComment(_.last(this.res.chats).chatManageId);
          }
        })
        .catch(err => {
          if (err.isCanceled) {
            return;
          }
          this.$store.dispatch('SHOW_ALERT', err.message);
        });
    },
    async approvalRequest(action, documents, overwriting = false) {
      // Types of Workのチェック
      if (_.every(this.cloneItems.processSeparate.typeOfWorksGrp, val => {
        return val !== TYPE_OF_WORKS.ON;
      })) {
        this.$store.dispatch('SHOW_ALERT', 'Please select at least one from Types of Work.');
        return;
      }
      this.$store.commit('START_PROCESS');
      // 商品のreferenceNoGrpをセット
      this.setReferenceNoGrp();
      const isDeleteRequest = this.tradeManagement.eventCancel || action === PROCESS_OPERATION.T_SAVING_DEL;
      let params;
      // 削除申請時は項目データを送信しない、バリデーションをしない
      if (isDeleteRequest || await this.checkValidate()) {
        if(this.isSimpleMode() || this.isStandardMode()) {
          const diffData = this.getDiffData(this.cloneItems, this.originalValue);
          // 輸出船積依頼承認依頼差分更新BFF
          params = {
            lslConfig: {
              serviceCode: 'tw-transaction-bff-api',
              apiCode: 'patch_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}',
              path: {
                entityId: this.entityId,
                processId: this.processId,
                processSeq: parseInt(this.processSeq)
              }
            },
            data: isDeleteRequest ? {
              meta: {
                tradingId: this.tradeManagement.tradingId,
                tradingFlowId: this.tradeManagement.tradingFlowId,
                updateDate: this.tradeManagement.updateDate,
                action: action,
                companyId: this.companyId
              },
              updateCommonItemsExclusiveFlag1: overwriting,
              updateCommonItemsEntitySpecificItemsExclusiveFlag2: overwriting,
              overwritePredecessorEntityExclusiveFlag3: overwriting,
              overwritingCommonItemsEntitySpecificItemsExclusiveFlag4: overwriting,
            } : _.omit({
              meta: {
                tradingId: this.tradeManagement.tradingId,
                tradingFlowId: this.tradeManagement.tradingFlowId,
                updateDate: this.tradeManagement.updateDate,
                action: action,
                companyId: this.companyId
              },
              updateCommonItemsExclusiveFlag1: overwriting,
              updateCommonItemsEntitySpecificItemsExclusiveFlag2: overwriting,
              overwritePredecessorEntityExclusiveFlag3: overwriting,
              overwritingCommonItemsEntitySpecificItemsExclusiveFlag4: overwriting,
              ...diffData,
              documents: documents,
            }, ['emailTransmissionHistories', 'chats', 'tradeManagement', 'requestApproveHisTrx', 'updateHistory'])
          };
        } else {
          // bff_ec_7 輸出船積依頼承認依頼BFF
          params = {
            lslConfig: {
              serviceCode: 'tw-transaction-bff-api',
              apiCode: 'post_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}',
              path: {
                entityId: this.entityId,
                processId: this.processId,
                processSeq: parseInt(this.processSeq)
              }
            },
            data: isDeleteRequest ? {
              meta: {
                tradingId: this.tradeManagement.tradingId,
                tradingFlowId: this.tradeManagement.tradingFlowId,
                updateDate: this.tradeManagement.updateDate,
                action: action,
                companyId: this.companyId
              },
              updateCommonItemsExclusiveFlag1: overwriting,
              updateCommonItemsEntitySpecificItemsExclusiveFlag2: overwriting,
              overwritePredecessorEntityExclusiveFlag3: overwriting,
              overwritingCommonItemsEntitySpecificItemsExclusiveFlag4: overwriting,
            } : _.omit({
              meta: {
                tradingId: this.tradeManagement.tradingId,
                tradingFlowId: this.tradeManagement.tradingFlowId,
                updateDate: this.tradeManagement.updateDate,
                action: action,
                companyId: this.companyId
              },
              updateCommonItemsExclusiveFlag1: overwriting,
              updateCommonItemsEntitySpecificItemsExclusiveFlag2: overwriting,
              overwritePredecessorEntityExclusiveFlag3: overwriting,
              overwritingCommonItemsEntitySpecificItemsExclusiveFlag4: overwriting,
              ...this.rejectHiddenGroups(),
              documents: documents,
            }, ['emailTransmissionHistories', 'chats', 'tradeManagement', 'requestApproveHisTrx', 'updateHistory'])
          };
        }

        $api
          .request(params)
          // eslint-disable-next-line no-unused-vars
          .then(res => {
            this.$store.commit('END_PROCESS');
            setTimeout(() => {
              this.$store.dispatch('SHOW_COMPLETED');
            }, 500);
            if (!this.isSimpleMode() || !this.isStandardMode() && this.$route.name.includes('EditProcess')) {
              this.$router.replace({
                name: `Process${_.capitalize(this.processId.replace(/\d/g, ''))}`,
                params: { entityId: this.entityId, processSeq: this.processSeq, _processId: this.processId.toLowerCase() }
              });
            }
            this.fetch();
          })
          .catch(err => {
            this.$store.commit('END_PROCESS');
            if (this.isConflict(err)) {
              this.$store.dispatch('SHOW_CONFIRM', err.message)
              .then(() => {
                this.approvalRequest(action, documents, true);
              })
              .catch(() => {});
            } else {
              this.$store.dispatch('SHOW_ALERT', err.message);
            }
          });
      } else {
        this.$store.commit('END_PROCESS');
        this.$store.dispatch('SHOW_ALERT', this.$t('Message.ERROR__INPUT_INVALID'));
      }
    },
    async save(action, documents, overwriting = false) {
      // Types of Workのチェック
      if (_.every(this.cloneItems.processSeparate.typeOfWorksGrp, val => {
        return val !== TYPE_OF_WORKS.ON;
      })) {
        this.$store.dispatch('SHOW_ALERT', 'Please select at least one from Types of Work.');
        return;
      }
      this.$store.commit('START_PROCESS');
      // 下書き仕様ののバリデーションにする
      const normalRules = this.createRules(schemas.tables, false);
      this.rules = this.createRules(schemas.tables, true);
      this.draftValid = true;

      let params;

      if (await this.checkValidate()) {
        if(this.isSimpleMode() || this.isStandardMode()) {
          const diffData = this.getDiffData(this.cloneItems, this.originalValue);
          //輸出船積依頼下書き保存差分更新BFF
          params = {
            lslConfig: {
              serviceCode: 'tw-transaction-bff-api',
              apiCode: 'patch_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}/wk',
              path: {
                entityId: this.entityId,
                processId: this.processId,
                processSeq: parseInt(this.processSeq)
              }
            },
            data:_.omit({
              meta: {
                tradingId: this.tradeManagement.tradingId,
                tradingFlowId: this.tradeManagement.tradingFlowId,
                updateDate: this.tradeManagement.updateDate,
                action: action,
                companyId: this.companyId
              },
              updateCommonItemsExclusiveFlag1: overwriting,
              updateCommonItemsEntitySpecificItemsExclusiveFlag2: overwriting,
              overwritePredecessorEntityExclusiveFlag3: overwriting,
              overwritingCommonItemsEntitySpecificItemsExclusiveFlag4: overwriting,
              ...diffData,
              documents: documents,
            }, ['emailTransmissionHistories', 'chats', 'tradeManagement', 'requestApproveHisTrx', 'updateHistory'])
          };
        } else {
          // bff_ec_5 輸出船積依頼下書き保存BFF
          params = {
            lslConfig: {
              serviceCode: 'tw-transaction-bff-api',
              apiCode: 'post_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}/wk',
              path: {
                entityId: this.entityId,
                processId: this.processId,
                processSeq: parseInt(this.processSeq)
              }
            },
            data:_.omit({
              meta: {
                tradingId: this.tradeManagement.tradingId,
                tradingFlowId: this.tradeManagement.tradingFlowId,
                updateDate: this.tradeManagement.updateDate,
                action: action,
                companyId: this.companyId
              },
              updateCommonItemsExclusiveFlag1: overwriting,
              updateCommonItemsEntitySpecificItemsExclusiveFlag2: overwriting,
              overwritePredecessorEntityExclusiveFlag3: overwriting,
              overwritingCommonItemsEntitySpecificItemsExclusiveFlag4: overwriting,
              ...this.rejectHiddenGroups(),
              documents: documents,
            }, ['emailTransmissionHistories', 'chats', 'tradeManagement', 'requestApproveHisTrx', 'updateHistory'])
          };
        }

        $api
          .request(params)
          // eslint-disable-next-line no-unused-vars
          .then(res => {
            this.$store.commit('END_PROCESS');
            setTimeout(() => {
              this.$store.dispatch('SHOW_COMPLETED');
            }, 500);
            if (!this.isSimpleMode() || !this.isStandardMode()) {
              this.$router.replace({
                name: `Process${_.capitalize(this.processId.replace(/\d/g, ''))}`,
                params: { entityId: this.entityId, processSeq: this.processSeq, _processId: this.processId.toLowerCase() }
              });
            }
            // 通常仕様のバリデーションに戻す
            this.rules = normalRules;
            this.draftValid = false;
            this.fetch();
          })
          .catch(err => {
            this.$store.commit('END_PROCESS');
            if (this.isConflict(err)) {
              this.$store.dispatch('SHOW_CONFIRM', err.message)
              .then(() => {
                // 通常仕様のバリデーションに戻す
                this.rules = normalRules;
                this.draftValid = false;
                this.save(action, documents, true);
              })
              .catch(() => {
                // 通常仕様のバリデーションに戻す
                this.rules = normalRules;
                this.draftValid = false;
              });
            } else {
              this.$store.dispatch('SHOW_ALERT', err.message);
              // 通常仕様のバリデーションに戻す
              this.rules = normalRules;
              this.draftValid = false;
            }
          });
      } else {
        this.$store.commit('END_PROCESS');
        this.$store.dispatch('SHOW_ALERT', this.$t('Message.ERROR__INPUT_INVALID'));
        // 通常仕様のバリデーションに戻す
        this.rules = normalRules;
        this.draftValid = false;
      }
    },
    approve(action, comment, to = false) {
      this.$store.commit('START_PROCESS');
      // bff_ec_8 輸出船積依頼承認BFF
      const params = {
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode: 'post_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}/approve',
          path: {
            entityId: this.entityId,
            processId: this.processId,
            processSeq: parseInt(this.processSeq)
          }
        },
        data: {
          tradingId: this.tradeManagement.tradingId,
          tradingFlowId: this.tradeManagement.tradingFlowId,
          updateDate: this.tradeManagement.updateDate,
          action: action,
          companyId: this.companyId,
          comment: comment,
        }
      };

      $api
        .request(params)
        // eslint-disable-next-line no-unused-vars
        .then(res => {
          this.$store.commit('END_PROCESS');
          setTimeout(async() => {
            this.$store.dispatch('SHOW_COMPLETED');
            if (to) {
              await new Promise((resolve) => setTimeout(resolve, 500));
              this.$router.push(to);
            }
          }, 500);
          if (!to) this.fetch();
        })
        .catch(err => {
          this.$store.commit('END_PROCESS');
          this.$store.dispatch('SHOW_ALERT', err.message);
        });
    },
    withdraw(action, comment, to = false) {
      this.$store.commit('START_PROCESS');
      // bff_ec_9 輸出船積依頼差戻・引戻・再確認依頼BFF
      const params = {
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode: 'post_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}/re',
          path: {
            entityId: this.entityId,
            processId: this.processId,
            processSeq: parseInt(this.processSeq)
          }
        },
        data: {
          tradingId: this.tradeManagement.tradingId,
          tradingFlowId: this.tradeManagement.tradingFlowId,
          updateDate: this.tradeManagement.updateDate,
          action: action,
          companyId: this.companyId,
          comment: comment,
        }
      };

      $api
        .request(params)
        // eslint-disable-next-line no-unused-vars
        .then(res => {
          this.$store.commit('END_PROCESS');
          setTimeout(async() => {
            this.$store.dispatch('SHOW_COMPLETED');
            if (to) {
              await new Promise((resolve) => setTimeout(resolve, 500));
              this.$router.push(to);
            }
          }, 500);
          if (!to) this.fetch();
        })
        .catch(err => {
          this.$store.commit('END_PROCESS');
          this.$store.dispatch('SHOW_ALERT', err.message);
        });
    },
    deleteDraft(comment) {
      this.$store.commit('START_PROCESS');
      // bff_ec_6 輸出船積依頼削除BFF
      const params = {
        lslConfig: {
          serviceCode: 'tw-transaction-bff-api',
          apiCode: 'post_/v1/request-ex-cc/{entityId}/{processId}/{processSeq}/delete',
          path: {
            entityId: this.entityId,
            processId: this.processId,
            processSeq: parseInt(this.processSeq)
          }
        },
        data: {
          tradingId: this.tradeManagement.tradingId,
          tradingFlowId: this.tradeManagement.tradingFlowId,
          updateDate: this.tradeManagement.updateDate,
          companyId: this.companyId,
          comment: comment,
        }
      };

      $api
        .request(params)
        // eslint-disable-next-line no-unused-vars
        .then(res => {
          this.$store.commit('END_PROCESS');
          setTimeout(() => {
            this.$store.dispatch('SHOW_COMPLETED');
          }, 500);
          this.fetch();
        })
        .catch(err => {
          this.$store.commit('END_PROCESS');
          this.$store.dispatch('SHOW_ALERT', err.message);
        });
    },
    register(action, documents) {
      action = this.tradeManagement.responsibleFlg === RESPONSIBLE_FLG.FROM ? PROCESS_OPERATION.F_APPROVE_REQ : PROCESS_OPERATION.T_APPROVE_REQ;
      this.approvalRequest(action, documents);
    },
    async checkValidate() {
      const lkResult = await this.validationCheck('form');
      const tsResult = await this.tsValidationCheck(this.$refs.form);
      return tsResult && lkResult;
    },
  }
};
</script>

<style lang="scss" scoped src="./ProcessDetail.scss"></style>
